import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import cx from "classnames";
import React, { useEffect, useState } from "react";
import { useController, useFormContext, useFormState } from "react-hook-form";
import {
  useGetDocumentLabel,
  useGetFieldLabel,
  useGetFieldPlaceholder,
} from "../../contexts/LanguageContext";
import {
  TApplicationField,
  TApplicationFieldOption,
} from "../../hooks/application-fields.hooks";
import { useUploadFile } from "../../hooks/applications.hooks";
import { useMediaQuery } from "../../hooks/common";
import { TDocumentConfig } from "../../hooks/forms.hooks";
import { VALIDATORS } from "../../utils/validators";
import { DOCUMENT_CONFIG_ID, RL_APPLICATION_TYPE_OPTION_ID, RL_APPLICATION_TYPE } from "../../config/phaseConstants";
import { DocumentPreviewDialog } from "../CommonComponents/DialogConfirmation/DocPreviewDialog";
import {
  DeleteButton,
  ViewButton,
} from "../CommonComponents/EditComponent/EditComponent";
import Input, { InputProps, TextArea } from "../CommonComponents/Input/Input";
import Select from "../CommonComponents/Select/Select";
import { ErrorToast } from "../toastMessage";
import { _axios } from "../../utils/axios";
import { useMoujaConfigs, getKhasaraRangeForMouja } from "../../hooks/mouja-khasara";
import APP_CONFIG from "../../config/appConfig";
type PropTypes = {
  disabled?: boolean;
  validator?: string;
  field: TApplicationField & {
    options?: TApplicationFieldOption[];
  };
};

function RenderInput({ validator, field, disabled }: PropTypes) {
  const { register, setValue } = useFormContext();
  const convert = useGetFieldLabel();
  const convertPlaceholder = useGetFieldPlaceholder();
  const info = useFormState({ name: field.uid });

  const isPlotNo = field.uid === "plot_no" || field.uid === "plot_number" || field.name === "Plot No";
  const isLayoutName =
    field.uid === "unauthorized_layout_name" ||
    field.uid === "layout_name" ||
    field.uid === "unauthorizedLayoutName" ||
    field.name === "Unauthorized Layout Name";

  let props: InputProps = {};
  let registerOpts: Parameters<typeof register>[1] = undefined;

  switch (field.type) {
    case "integer":
    case "area":
    case "float": {
      const blockInvalidChar: React.KeyboardEventHandler<HTMLInputElement> = (
        e
      ) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();

      props = { type: "number", onKeyDown: blockInvalidChar };
      registerOpts = {
        valueAsNumber: true,
        min: { value: 0, message: "Cannot be less than 0" },
      };
      break;
    }
    case "date": {
      props = { type: "date" };
      registerOpts = { valueAsDate: true };
      break;
    }
  }
  return (
    <Input
      label={convert(field)}
      required={!!field?.mandatory}
      error={!!info?.errors?.[field.uid]}
      placeholder={convertPlaceholder(field)}
      disabled={disabled ?? false}
      helperText={info?.errors?.[field.uid]?.message?.toString?.() || ""}
      {...props}
      {...register(field.uid, {
        required: field.mandatory ? "* Required" : false,
        ...(isPlotNo && {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            let cleanVal = val.replace(/[^0-9]/g, "");
            cleanVal = cleanVal.replace(/^0+(?=\d)/, "");
            setValue(field.uid, cleanVal, { shouldValidate: true });
          },
        }),
        ...(isLayoutName && {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            const cleanVal = val.replace(/[^a-zA-Z0-9 ]/g, "");
            setValue(field.uid, cleanVal, { shouldValidate: true });
          },
        }),
        ...(!!validator &&
          !!VALIDATORS[validator] && { validate: VALIDATORS[validator] }),
        ...registerOpts,
      })}
    />
  );
}

function RenderArea({ field, disabled }: PropTypes) {
  const { register, getValues } = useFormContext();
  const convert = useGetFieldLabel();
  const convertPlaceholder = useGetFieldPlaceholder();
  const info = useFormState({ name: field.uid });

  let options = ["sq. mt.", "sq. ft."];

  const unitName = `unit_${field.uid}`;

  const blockInvalidChar: React.KeyboardEventHandler<HTMLInputElement> = (e) =>
    ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();

  const props = { type: "number", onKeyDown: blockInvalidChar };
  const registerOpts = { valueAsNumber: true };

  return (
    <div className="flex items-end justify-between gap-4">
      <Input
        label={convert(field)}
        containerClass="basis-2/4 md:basis-3/4"
        required={!!field?.mandatory}
        error={!!info?.errors?.[field.uid]}
        placeholder={convertPlaceholder(field)}
        disabled={disabled ?? false}
        helperText={info?.errors?.[field.uid]?.message?.toString?.() || ""}
        {...props}
        {...register(field.uid, {
          required: field.mandatory ? "* Required" : false,
          ...registerOpts,
        })}
      />
      {/*
       <Select
        containerClass="basis-2/4 md:basis-1/4"
        error={!!info?.errors?.[unitName]}
        disabled={disabled}
        required={!!field?.mandatory}
        defaultValue={""}
        helperText={info?.errors?.[unitName]?.message?.toString?.() || ""}
        {...register(unitName, {
          validate: (v) => {
            if (getValues(field.name) && !v) return "* Required";
            return true;
          },
        })}
      >
        <option hidden></option>
        {options?.map((e) => (
          <option key={`${field.id}-${e}`} value={e}>
            {e}
          </option>
        ))}
      </Select>
      */}
      <Input
        containerClass="basis-2/4 md:basis-1/4"
        value="sq. mt."
        disabled={true}
        readOnly={true}
        {...register(unitName, { value: "sq. mt." })}
      />
    </div>
  );
}

function RenderSelect({ field, disabled }: PropTypes) {
  const { register, getValues, setValue } = useFormContext();
  const convert = useGetFieldLabel();
  const convertPlaceholder = useGetFieldPlaceholder();
  const info = useFormState({ name: field.uid });

  const [dbMoujaOptions, setDbMoujaOptions] = useState<any[]>([]);

  const isMaujaField = field.uid === "mauja" || field.name === "Mauja" || String(field.uid).toLowerCase().includes("mauje");

  const isConstructionField = field.uid === "is_there_a_construction";
  const enableConstructionField = Boolean((APP_CONFIG as any)?.ENABLE_CONSTRUCTION_QUESTION);
  const shouldDisableConstruction = isConstructionField && !enableConstructionField;

  useEffect(() => {
    if (shouldDisableConstruction) {
      setValue(field.uid, false, { shouldValidate: true });
    }
  }, [shouldDisableConstruction, field.uid, setValue]);

  useEffect(() => {
    if (isMaujaField) {
      _axios
        .get("/admin/mouja-asr-config")
        .then((res: any) => {
          if (res.data && res.data.data && Array.isArray(res.data.data)) {
            const mapped = res.data.data.map((m: any) => ({
              id: m.mouja_name,
              name: m.mouja_name,
              active: true,
            }));
            setDbMoujaOptions(mapped);
          }
        })
        .catch((err: any) => console.error("Error fetching Mouja options:", err));
    }
  }, [isMaujaField]);

  let options: { id: number | boolean | string; name: string; active?: boolean }[] =
    field.type === "boolean"
      ? [
        { id: true, name: "Yes", active: true },
        { id: false, name: "No", active: true },
      ]
      : isMaujaField
        ? dbMoujaOptions
        : field?.options || [];
  let registerOpts: Parameters<typeof register>[1] = undefined;
  const optionsAreNumeric =
    options.length > 0 && options.every((o) => o.id !== "" && !isNaN(Number(o.id)));
  console.log("getValues", getValues())

  // 🌐 PERMANENT DYNAMIC SOLUTION: Works for ALL present & future dropdown fields!
  const rawValue = getValues(field.uid);

  useEffect(() => {
    if (shouldDisableConstruction) return;
    if (field.type === "boolean" && (rawValue === 1 || rawValue === "1" || rawValue === 0 || rawValue === "0")) {
      const boolVal = rawValue === 1 || rawValue === "1";
      setValue(field.uid, boolVal, { shouldValidate: true });
      return;
    }
    if (rawValue !== undefined && rawValue !== null && rawValue !== "" && options?.length > 0) {
      const valStr = String(rawValue).trim().toLowerCase();

      // Dynamically find matching option by ID, Exact Name, or Partial Name (Zero Hardcoding)
      const matched = options.find((opt) => {
        const optIdStr = String(opt.id ?? "").trim().toLowerCase();
        const optNameStr = String(opt.name ?? "").trim().toLowerCase();

        return (
          optIdStr === valStr ||
          optNameStr === valStr ||
          (valStr.length > 2 && optNameStr.includes(valStr)) ||
          (optNameStr.length > 2 && valStr.includes(optNameStr))
        );
      });

      if (matched) {
        setValue(field.uid, String(matched.id), { shouldValidate: false });
      }
    }
  }, [rawValue, options, field.uid, field.type, setValue, shouldDisableConstruction]);




  return (
    <Select
      label={convert(field)}
      error={!!info?.errors?.[field.uid]}
      // placeholder={convertPlaceholder(field)}
      disabled={field.uid === "name_of_service" || shouldDisableConstruction || disabled}
      required={!!field?.mandatory}
      value={rawValue !== undefined && rawValue !== null ? String(rawValue) : ""}
      {...register(field.uid, {
        ...(shouldDisableConstruction && { value: false }),
        ...(isMaujaField && {
          onChange: (e: any) => {
            const val = e.target.value;
            setValue("mauja", val, { shouldValidate: true });
          },
        }),
        ...(field.type === "boolean"
          ? {
            validate: (v) => {
              if (v === true || v === false || v === 1 || v === 0 || v === "1" || v === "0" || !field.mandatory) return true;
              return "* Required";
            },
            setValueAs: (v) => {
              if (v === true || v === "true" || v === 1 || v === "1") return true;
              if (v === false || v === "false" || v === 0 || v === "0") return false;
              return "";
            },
          }
          : isMaujaField
            ? {
              required: field.mandatory ? "* Required" : false,
            }
            : {
              valueAsNumber: true,
              required: field.mandatory ? "* Required" : false,
            }),
      })}
    >
      <option value="" disabled hidden>
        {convertPlaceholder(field) || "Select Option"}
      </option>
      {options
        ?.filter((e) => e.active)
        ?.map((e) => (
          <option key={`${field.id}-${e.id}`} value={String(e.id)}>
            {e.name}
          </option>
        ))}
    </Select>
  );
}

function
  RenderTextarea({ validator, field, disabled }: PropTypes) {
  const { register } = useFormContext();
  const convert = useGetFieldLabel();
  const convertPlaceholder = useGetFieldPlaceholder();
  const info = useFormState({ name: field.uid });

  return (
    <TextArea
      label={convert(field)}
      required={!!field?.mandatory}
      error={!!info?.errors?.[field.uid]}
      placeholder={convertPlaceholder(field)}
      disabled={disabled ?? false}
      rows={4}
      helperText={info?.errors?.[field.uid]?.message?.toString?.() || ""}
      {...register(field.uid, {
        required: field.mandatory ? "* Required" : false,
        ...(!!validator &&
          !!VALIDATORS[validator] && { validate: VALIDATORS[validator] }),
      })}
    />
  );
}

// Shared by RenderKhasaraSelect and RenderKhasaraManualInput so they can be
// rendered as two independent grid cells (see MaujaKhasaraPlotBlock in
// RenderStep.tsx) while staying in sync -- both read/write the same RHF
// fields (manual_khasara_no, main_khasara_no, the combined khasara uid)
// reactively via watch(), so neither owns a local copy that could drift
// out of sync with the other.
function useKhasaraFieldState(fieldUid: string) {
  const { setValue, watch } = useFormContext();
  const moujaConfigs = useMoujaConfigs();

  const selectedMauja = watch("mauja");
  const { minLimit, maxLimit, maujaDisplayName, khasaraOptions } =
    getKhasaraRangeForMouja(moujaConfigs, selectedMauja);

  const manualKhasara = watch("manual_khasara_no") || watch(fieldUid)?.split("/")?.slice(1)?.join("/") || "";
  const mainKhasaraVal = watch(`${fieldUid}_main`) || watch(fieldUid)?.split("/")?.[0] || "";

  const handleKhasaraCombine = (mainVal: string, manualVal: string) => {
    const cleanManual = manualVal.trim();
    setValue("manual_khasara_no", cleanManual);
    setValue("main_khasara_no", mainVal);

    if (mainVal && cleanManual) {
      setValue(fieldUid, `${mainVal}/${cleanManual}`, { shouldValidate: true });
    } else if (mainVal) {
      setValue(fieldUid, mainVal, { shouldValidate: true });
    } else if (cleanManual) {
      setValue(fieldUid, cleanManual, { shouldValidate: true });
    }
  };

  useEffect(() => {
    if (mainKhasaraVal && !watch("main_khasara_no")) {
      setValue("main_khasara_no", mainKhasaraVal);
    }
  }, [mainKhasaraVal]);

  return {
    selectedMauja,
    minLimit,
    maxLimit,
    maujaDisplayName,
    khasaraOptions,
    manualKhasara,
    mainKhasaraVal,
    handleKhasaraCombine,
  };
}

export function RenderKhasaraSelect({ field, disabled }: PropTypes) {
  const convert = useGetFieldLabel();
  const info = useFormState({ name: field.uid });
  const { setValue } = useFormContext();
  const {
    selectedMauja,
    minLimit,
    maxLimit,
    maujaDisplayName,
    khasaraOptions,
    manualKhasara,
    mainKhasaraVal,
    handleKhasaraCombine,
  } = useKhasaraFieldState(field.uid);

  return (
    <Select
      label={`${convert(field)} (Select Main Khasara)`}
      required={!!field?.mandatory}
      error={!!info?.errors?.[field.uid]}
      disabled={disabled || !selectedMauja}
      helperText={!selectedMauja ? "Please select Mouja first" : info?.errors?.[field.uid]?.message?.toString?.() || ""}
      value={mainKhasaraVal}
      onChange={(e: any) => {
        const val = e.target.value;
        setValue(`${field.uid}_main`, val);
        handleKhasaraCombine(val, manualKhasara);
      }}
    >
      <option value="" disabled hidden>
        {selectedMauja ? `Select Khasara (${minLimit} to ${maxLimit})` : "Please select Mouja first"}
      </option>
      {khasaraOptions.map((num) => (
        <option key={`khasara-${num}`} value={String(num)}>
          Khasara No. {num} ({maujaDisplayName})
        </option>
      ))}
    </Select>
  );
}

export function RenderKhasaraManualInput({
  khasaraFieldUid,
  disabled,
}: {
  khasaraFieldUid: string;
  disabled?: boolean;
}) {
  const { manualKhasara, mainKhasaraVal, handleKhasaraCombine } =
    useKhasaraFieldState(khasaraFieldUid);

  return (
    <Input
      label="Manual Sub-Khasara (e.g. 55/65)"
      placeholder="Enter manual sub-khasara text"
      value={manualKhasara}
      disabled={disabled ?? false}
      onChange={(e) => {
        const val = e.target.value.replace(/[^0-9\/a-zA-Z]/g, "");
        handleKhasaraCombine(mainKhasaraVal, val);
      }}
      onBlur={() => handleKhasaraCombine(mainKhasaraVal, manualKhasara)}
    />
  );
}

export function RenderField({ field, disabled }: PropTypes) {
  const { watch } = useFormContext();
  const forceReadonly =
    field.uid === "type_of_application_submitter" ||
    field.uid === "submitted_by";
  const isDisabled = disabled || forceReadonly;

  const enableConstructionQuestion = Boolean((APP_CONFIG as any)?.ENABLE_CONSTRUCTION_QUESTION);
  const isConstructionAreaField = field.uid === "construction_area" || field.uid === "built_up_area";
  const constructionValue = watch("is_there_a_construction");

  // 🔒 Hide construction_area when feature flag is disabled OR construction is No (false)
  if (isConstructionAreaField) {
    if (
      !enableConstructionQuestion ||
      constructionValue === false ||
      constructionValue === 0 ||
      constructionValue === "0" ||
      constructionValue === "false"
    ) {
      return null;
    }
  }

  if (field.uid === "khasara_no" || field.uid === "main_khasara_no") {
    return <RenderKhasaraSelect field={field} disabled={isDisabled} />;
  }

  // Fallback only: normally Manual Sub-Khasara is rendered directly by
  // MaujaKhasaraPlotBlock (RenderStep.tsx) as its own field, since the
  // backend doesn't register "manual_khasara_no" as a real field. This
  // branch only fires if RenderStep injects a synthetic entry because a
  // service is missing mauja/khasara_no/plot_no as a full trio.
  if (field.uid === "manual_khasara_no") {
    return <RenderKhasaraManualInput khasaraFieldUid="khasara_no" disabled={isDisabled} />;
  }

  switch (field.type) {
    case "textarea":
      return (
        <RenderTextarea
          validator={field.validator}
          disabled={isDisabled}
          field={field}
        />
      );
    case "boolean":
    case "configurable-select":
    case "select":
      return <RenderSelect field={field} disabled={isDisabled} />;
    case "area":
      return <RenderArea field={field} disabled={isDisabled} />;
    case "integer":
    case "float":
    case "date":
    default:
      return (
        <RenderInput
          validator={field.validator}
          disabled={isDisabled}
          field={field}
        />
      );
  }
}

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export function RenderDocumentConfigs({
  configs,
}: {
  configs: TDocumentConfig[];
}) {
  const { getValues } = useFormContext();
  const fieldValue = getValues("is_there_a_construction");
  const rlApplicationType = getValues("rl_application_type");
  console.log("get field", fieldValue)
  console.log("configs", configs);
  if (!configs.length) return null;

  return (
    <div className="mx-auto max-w-[1000px]">
      <table className="table-fixed w-full">
        <thead className="border-b border-dashed">
          <tr>
            <th className="w-[60%] p-3 text-xl border-r">Document Name</th>
            <th className="w-[40%] p-3 text-xl text-center">Upload</th>
          </tr>
        </thead>
        <tbody>
          {configs
            .filter((config) => {
              // Show the document Building Plan if fieldValue is true
              if (config.document_config_id === 1) {
                return fieldValue === true;
              }
              // Show the document Site Plan if fieldValue is false
              if (config.document_config_id === 2) {
                return fieldValue === false;
              }
              // K Prat / Registry Certificate are only required for Permanent RL applications
              if (
                config.document_config_id === DOCUMENT_CONFIG_ID.PERMANENT_RL_K_PRAT ||
                config.document_config_id === DOCUMENT_CONFIG_ID.PERMANENT_RL_REGISTRY_CERT
              ) {
                const strAppType = String(rlApplicationType ?? "").trim().toLowerCase();
                const isPermanent =
                  rlApplicationType === RL_APPLICATION_TYPE_OPTION_ID.PERMANENT ||
                  rlApplicationType === RL_APPLICATION_TYPE.PERMANENT ||
                  strAppType === "9993" ||
                  strAppType === "permanent";
                return isPermanent;
              }
              // By default, return all other documents
              return true;
            })
            // .map((e) => (
            //   <RenderDocumentConfig key={e.document_config_id} config={e} />
            // ))}
            .map((config) => (
              <RenderDocumentConfig
                key={config.document_config_id}
                config={{
                  ...config,
                  mandatory: config.document_config_id === 1 || config.document_config_id === 2 ? 1 : config.mandatory
                }}
              />
            ))}
        </tbody>
      </table>
      <p style={{ color: 'gray', fontStyle: 'italic', marginTop: '15px', marginBottom: '15px', textAlign: 'center' }}>
        Note: Please upload clear and legible documents
      </p>
    </div>
  );
}

export function RenderDocumentConfig({ config }: { config: TDocumentConfig }) {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const uploadMutation = useUploadFile();
  const [previewDocument, setPreviewDocument] = useState<{
    url: string;
  } | null>(null);
  const document = useController({
    name: `documents.${config.document_config_id}`,
    rules: { required: config.mandatory ? "* Required" : false },
  });
  const getDocumentLabel = useGetDocumentLabel();
  const onUpload = async (file: File) => {
    try {
      if (config.file_type) {
        const types = config.file_type.split(",").filter((e) => !!e);
        if (!types.some((e) => file.type.includes(e))) {
          return ErrorToast(
            "Invalid file select, please select a different file!"
          );
        }
      }
      if (
        config.file_size &&
        ["KB", "MB"].includes(config?.file_size_unit || "")
      ) {
        let size = +config.file_size;
        const sizeInMB = file.size / (1024 * 1024);
        if (config.file_size_unit === "KB") {
          size /= 1024;
        }

        if (sizeInMB > size) {
          return ErrorToast(
            `Please select a small file, file cannot be more than ${config.file_size} ${config.file_size_unit}`
          );
        }
      }

      const result = await uploadMutation.mutateAsync(file);
      const url = result?.data?.data;
      if (!url) throw new Error();
      document.field.onChange(url);
    } catch (err) {
      ErrorToast("Something went wrong while uploading file!");
    }
  };

  return (
    <>
      <tr>
        <td className="p-3 border-r">
          <p className="grow text-grey-600">
            {/* {config.document_label} */}
            {getDocumentLabel(config)}
            {config.mandatory ? <span className="text-red-500"> *</span> : null}
          </p>
        </td>
        <td className="flex space-x-6 items-center justify-center p-3 border-0">
          {!document.field?.value ? (
            isMobile ? (
              <div>
                <label
                  htmlFor={document.field.name}
                  className={cx("bg-primary px-4 py-2 rounded block", {
                    ["border-red-500 border"]: document.fieldState?.error,
                  })}
                >
                  <i className="fa fa-upload m-0" />
                  <input
                    type="file"
                    id={document.field.name}
                    name={document.field.name}
                    onChange={(e) => {
                      const hit = e.target.files?.[0];
                      if (hit) onUpload(hit);
                    }}
                  />
                </label>
                {!!document?.fieldState?.error && (
                  <span className="text-xs text-red-500">
                    {document?.fieldState?.error?.message?.toString()}
                  </span>
                )}
              </div>
            ) : (
              <Input
                type="file"
                ref={document.field.ref}
                onBlur={document.field.onBlur}
                disabled={uploadMutation.isPending}
                error={!!document.fieldState?.error}
                onChange={(e) => {
                  const hit = e.target.files?.[0];
                  if (hit) onUpload(hit);
                }}
                helperText={document?.fieldState?.error?.message?.toString()}
                className="file:-my-3 file:-ml-4 file:h-[50px] file:border-0 file:mr-4 file:bg-primary file:px-4"
              />
            )
          ) : (
            <>
              <div className="flex justify-center space-x-1">
                <ViewButton
                  onClick={() => {
                    // setPreviewDocument({ url: document.field.value });
                    openInNewTab(document.field.value);
                  }}
                />
                <DeleteButton onClick={() => document.field.onChange(null)} />
                {/* <button onClick={() => openInNewTab(document.field.value)}>
              <i className="text-xl m-0 fa fa-eye"></i>
            </button>
            <button onClick={() => document.field.onChange(null)}>
              <i className="text-xl m-0 fa fa-trash-o"></i>
            </button> */}
              </div>
            </>
          )}
        </td>
      </tr>
      <DocumentPreviewDialog
        onClose={() => setPreviewDocument(null)}
        open={!!previewDocument}
      >
        {previewDocument &&
          previewDocument.url &&
          (previewDocument.url.endsWith("pdf") ? (
            <Worker
              workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}
            >
              <Viewer fileUrl={previewDocument.url} />
            </Worker>
          ) : (
            <img
              src={previewDocument.url}
              className="object-contain w-full h-full"
              alt="Document"
            />
          ))}
      </DocumentPreviewDialog>
    </>
  );
}
